import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Search, Loader2, Bell, RefreshCw, Eye } from "lucide-react";
import { toast } from "sonner";
import { postFindLead, getBudgetGuidance } from "@/lib/api";
import { openEmail } from "@/lib/contact";

const PLACEHOLDERS = [
  "2021 Toyota Tacoma",
  "2020 BMW M4",
  "2023 Ford Bronco",
  "2022 Mercedes GLE",
  "2019 Porsche 911",
  "2023 Rivian R1T",
];

function useRotatingPlaceholder() {
  const [idx, setIdx] = useState(0);
  const [display, setDisplay] = useState("");
  const [typing, setTyping] = useState(true);

  useEffect(() => {
    const full = PLACEHOLDERS[idx];
    let t;
    if (typing) {
      if (display.length < full.length) {
        t = setTimeout(() => setDisplay(full.slice(0, display.length + 1)), 55);
      } else {
        t = setTimeout(() => setTyping(false), 1400);
      }
    } else {
      if (display.length > 0) {
        t = setTimeout(() => setDisplay(display.slice(0, -1)), 30);
      } else {
        setTyping(true);
        setIdx((i) => (i + 1) % PLACEHOLDERS.length);
      }
    }
    return () => clearTimeout(t);
  }, [display, typing, idx]);

  return display;
}

const emptyForm = {
  vehicle_type: "",
  make: "",
  model: "",
  year: "",
  trim: "",
  budget: "",
  mileage_pref: "",
  color: "",
  features: "",
  location: "",
  name: "",
  email: "",
  phone: "",
  notes: "",
  send_copy: true,
};

export default function FindMyCar() {
  const [search, setSearch] = useState("");
  const placeholder = useRotatingPlaceholder();
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [guidance, setGuidance] = useState(null);
  const [guidanceLoading, setGuidanceLoading] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSearchGo = () => {
    const parts = search.trim().split(/\s+/);
    if (parts.length >= 2) {
      const [year, make, ...rest] = parts;
      if (/^\d{4}$/.test(year)) {
        setField("year", year);
        setField("make", make || "");
        setField("model", rest.join(" "));
      } else {
        setField("make", parts[0]);
        setField("model", parts.slice(1).join(" "));
      }
    }
    document.getElementById("request-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const runBudgetCheck = async () => {
    if (!form.make || !form.model || !form.budget) {
      toast.error("Add make, model and budget to check market range");
      return;
    }
    setGuidanceLoading(true);
    setGuidance(null);
    try {
      const budgetNum = parseFloat(form.budget.toString().replace(/[^0-9.]/g, ""));
      const res = await getBudgetGuidance({
        make: form.make,
        model: form.model,
        year: form.year || undefined,
        budget: budgetNum,
      });
      setGuidance(res);
    } catch (e) {
      toast.error("Couldn't fetch market range. Send your request and we'll follow up directly.");
    } finally {
      setGuidanceLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.make || !form.model || !form.name || !form.email || !form.phone) {
      toast.error("Please fill in make, model and contact info");
      return;
    }
    setLoading(true);
    try {
      await postFindLead(form);
      // Also open a pre-filled email from the visitor's own mail app -
      // lands straight in your personal inbox, no server key needed.
      openEmail({
        subject: `Find My Car · ${form.year || ""} ${form.make} ${form.model}`.trim(),
        cc: form.send_copy ? form.email : undefined,
        body: [
          "New Find My Car request from strivemotorsfl.com",
          "",
          `Name:      ${form.name}`,
          `Email:     ${form.email}`,
          `Phone:     ${form.phone}`,
          "",
          `Vehicle:   ${form.year || ""} ${form.make} ${form.model} ${form.trim || ""}`.trim(),
          `Type:      ${form.vehicle_type || "-"}`,
          `Budget:    ${form.budget || "-"}`,
          `Mileage:   ${form.mileage_pref || "-"}`,
          `Color:     ${form.color || "-"}`,
          `Features:  ${form.features || "-"}`,
          `Location:  ${form.location || "-"}`,
          "",
          `Notes:     ${form.notes || "-"}`,
        ].join("\n"),
      });
      toast.success(
        "Request saved · a pre-filled email opened in your mail app - tap Send to alert Strive instantly."
      );
      setForm(emptyForm);
      setGuidance(null);
      setSearch("");
    } catch (err) {
      toast.error("Something went wrong. Please call us directly at 561-502-6769.");
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n) => `$${Math.round(n).toLocaleString()}`;

  return (
    <section id="find" data-testid="find-section" className="relative py-28 lg:py-40 px-6 lg:px-10 bg-[#0A0A0A]">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="flex items-baseline gap-4 mb-14">
          <span className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#E31E24]">
            (02) · Request
          </span>
          <span className="flex-1 h-px bg-white/15 mt-1" />
        </div>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="font-anton uppercase leading-[0.9] text-[10vw] md:text-[7vw] lg:text-[5.2vw] mb-6 max-w-5xl"
        >
          Any car. <span className="font-cormorant italic font-medium text-[#E31E24]">any budget.</span> we help you find it.
        </motion.h2>
        <p className="text-white/60 max-w-2xl mb-14 text-[15px] lg:text-lg leading-relaxed">
          Tell us what you're looking for and we'll search our network to find the best available options.
        </p>

        {/* Big search bar */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="relative flex items-center gap-4 border border-white/15 bg-[#141414]/60 backdrop-blur-md px-4 lg:px-6 py-4 lg:py-5 mb-20 focus-within:border-[#E31E24] transition-colors duration-300"
        >
          <Search size={22} className="text-white/60 shrink-0" aria-hidden="true" />
          <div className="relative flex-1">
            <label htmlFor="vehicle-search" className="sr-only">Search any vehicle</label>
            <input
              id="vehicle-search"
              data-testid="hero-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearchGo()}
              className="w-full bg-transparent outline-none text-lg lg:text-2xl font-anton uppercase tracking-wider placeholder-transparent"
              placeholder="Search any vehicle..."
              aria-label={`Search any vehicle. Example: ${placeholder}`}
            />
            {!search && (
              <div className="absolute inset-0 pointer-events-none flex items-center text-lg lg:text-2xl font-anton uppercase tracking-wider text-white/50" aria-hidden="true">
                {placeholder}
                <span className="caret" />
              </div>
            )}
          </div>
          <button
            onClick={handleSearchGo}
            data-testid="hero-search-go"
            className="hidden sm:inline-flex items-center gap-2 bg-[#E31E24] hover:bg-white hover:text-[#0A0A0A] px-5 py-3 font-anton uppercase tracking-[0.12em] transition-colors duration-300"
          >
            Search <ArrowUpRight size={18} />
          </button>
        </motion.div>

        {/* Vehicle Request Form */}
        <div id="request-form" className="grid grid-cols-12 gap-10 lg:gap-16">
          <div className="col-span-12 lg:col-span-4">
            <div className="sticky top-32">
              <div className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#E31E24] mb-4">
                (Form) · Vehicle Request
              </div>
              <h3 className="font-anton uppercase text-4xl lg:text-6xl leading-[0.9] mb-6">
                Find any vehicle you want
              </h3>
              <p className="text-white/65 leading-relaxed text-[15px] mb-8">
                Can't find the right vehicle? Let us search for it. Fill in what you know · we'll
                handle the rest.
              </p>
              <div className="space-y-4 border-l border-[#E31E24] pl-5">
                <div className="text-white/55 text-sm font-mono2 uppercase tracking-widest">
                  What we'll do
                </div>
                <ul className="text-white/80 space-y-2 text-[15px]">
                  <li>· Curate real, available matches</li>
                  <li>· Verify condition & history</li>
                  <li>· Report back with the best options</li>
                </ul>
              </div>
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid="find-form"
            className="col-span-12 lg:col-span-8 grid grid-cols-2 gap-4"
          >
            {[
              ["make", "Make *", "e.g. Porsche"],
              ["model", "Model *", "e.g. Cayenne"],
              ["year", "Year", "e.g. 2023"],
              ["trim", "Trim", "e.g. GTS"],
              ["budget", "Budget (USD)", "e.g. 55000"],
              ["mileage_pref", "Mileage Preference", "e.g. under 40k"],
              ["color", "Color", "e.g. Black"],
              ["vehicle_type", "Vehicle Type", "SUV / Sedan / Truck"],
              ["features", "Features", "e.g. Pano roof, AWD", "col-span-2"],
              ["location", "Location", "City, State", "col-span-2"],
              ["name", "Name *", "Your full name"],
              ["email", "Email *", "you@example.com"],
              ["phone", "Phone *", "561-000-0000", "col-span-2"],
              ["notes", "Notes", "Anything else we should know?", "col-span-2"],
            ].map(([name, label, ph, extra]) => (
              <label
                key={name}
                className={`flex flex-col gap-2 border-b border-white/15 focus-within:border-[#E31E24] pb-2 transition-colors ${extra || "col-span-2 sm:col-span-1"}`}
              >
                <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-white/70">
                  {label}
                </span>
                <input
                  data-testid={`find-input-${name}`}
                  name={name}
                  value={form[name] || ""}
                  onChange={(e) => setField(name, e.target.value)}
                  placeholder={ph}
                  type={name === "email" ? "email" : "text"}
                  className="bg-transparent outline-none text-white text-[15px] placeholder-white/50 py-1"
                />
              </label>
            ))}

            {/* Smart budget guidance trigger */}
            <div className="col-span-2 flex flex-wrap items-center gap-3 mt-2">
              <button
                type="button"
                onClick={runBudgetCheck}
                disabled={guidanceLoading}
                data-testid="budget-check-btn"
                className="inline-flex items-center gap-2 border border-white/30 hover:border-[#E31E24] hover:text-[#E31E24] px-4 py-2 font-mono2 uppercase text-[11px] tracking-[0.24em] transition-colors"
              >
                {guidanceLoading ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                Check market range
              </button>
              <span className="text-white/60 text-xs font-mono2">
                AI-powered budget reality check
              </span>
            </div>

            {/* Guidance card */}
            <AnimatePresence>
              {guidance && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  data-testid="budget-guidance"
                  className="col-span-2 border border-[#E31E24]/60 bg-[#E31E24]/8 p-6"
                >
                  <div className="flex items-start justify-between gap-6 flex-wrap">
                    <div>
                      <div className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#E31E24] mb-2">
                        Market Guidance · {guidance.verdict}
                      </div>
                      <p className="text-white/85 max-w-2xl leading-relaxed">{guidance.message}</p>
                      <div className="mt-4 font-anton uppercase text-xl">
                        Estimated Market Range -{" "}
                        <span className="text-[#E31E24]">
                          {fmt(guidance.estimated_min)}–{fmt(guidance.estimated_max)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => toast.info("We'll include in-budget vehicles in your matches.")}
                      className="inline-flex items-center gap-2 bg-[#E31E24] hover:bg-white hover:text-[#0A0A0A] px-4 py-2.5 font-mono2 uppercase text-[11px] tracking-[0.24em]"
                    >
                      <Eye size={14} /> See vehicles in my budget
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setField("budget", Math.round(guidance.estimated_min).toString());
                        toast.success("Budget adjusted to estimated market minimum.");
                      }}
                      className="inline-flex items-center gap-2 border border-white/40 hover:border-white px-4 py-2.5 font-mono2 uppercase text-[11px] tracking-[0.24em]"
                    >
                      <RefreshCw size={14} /> Adjust my budget
                    </button>
                    <button
                      type="button"
                      onClick={() => toast.success("You'll be notified when one hits your price.")}
                      className="inline-flex items-center gap-2 border border-white/40 hover:border-white px-4 py-2.5 font-mono2 uppercase text-[11px] tracking-[0.24em]"
                    >
                      <Bell size={14} /> Notify me when one hits my price
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="col-span-2 mt-2">
              <label className="inline-flex items-center gap-3 cursor-pointer select-none group">
                <input
                  type="checkbox"
                  checked={form.send_copy}
                  onChange={(e) => setField("send_copy", e.target.checked)}
                  data-testid="find-send-copy"
                  className="sr-only peer"
                />
                <span className="w-5 h-5 border border-white/40 peer-checked:bg-[#E31E24] peer-checked:border-[#E31E24] flex items-center justify-center transition-colors">
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className={form.send_copy ? "opacity-100" : "opacity-0"}>
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
                <span className="text-white/80 text-sm">Send me a copy of this request</span>
              </label>
            </div>

            <div className="col-span-2 mt-4">
              <button
                type="submit"
                disabled={loading}
                data-testid="find-submit"
                className="group w-full sm:w-auto inline-flex items-center justify-between gap-6 bg-[#E31E24] hover:bg-white hover:text-[#0A0A0A] text-white px-8 py-5 transition-colors duration-300"
              >
                <span className="font-anton uppercase tracking-[0.14em] text-xl">
                  {loading ? "Sending..." : "Send my request"}
                </span>
                {loading ? (
                  <Loader2 size={22} className="animate-spin" />
                ) : (
                  <ArrowUpRight size={22} className="transition-transform group-hover:rotate-45" />
                )}
              </button>
              <div className="mt-4 text-white/60 text-xs font-mono2 uppercase tracking-widest">
                We respond within 24 hours · No obligation · A pre-filled email will open in your mail app
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
